import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiUrl } from '../utils/apiBase';
import { pluralPL } from '../utils/pluralPL';

interface SeasonRoundResult {
  roundId: number;
  kolejkaNumber: number;
  groupName: string | null;
  roundDate: string;
  position: number;
  points: number;
  counted: boolean;
}

interface SeasonStandingEntry {
  entityId: number;
  rank?: number;
  name: string;
  playerIds: number[];
  playerRatings?: (number | null)[];
  groupName: string | null;
  roundsPlayed: number;
  countedRounds: number;
  countedPoints: number;
  totalPoints: number;
  perRoundPoints: number[];
  results: SeasonRoundResult[];
}

interface SeasonStandingsMeta {
  roundsTotal: number;
  roundsPlayed: number;
  seasonTarget: number;
  scoringRule: string;
  // Starsze API ich nie wysyła — wtedy liga liczy klasycznie, 77%.
  classificationSystem?: 'PLACEMENT' | 'MATCH_POINTS';
  // Null = sezon ma własną liczbę kolejek (`seasonTarget`), nie procent.
  countedPercent?: number | null;
  ratingDiscipline?: 'SINGLES' | 'DOUBLES';
}

interface LeagueSeasonItem {
  id: number;
  name: string;
  number: number;
}

interface LeagueMeta {
  name: string;
  rounds: unknown[];
  seasons?: LeagueSeasonItem[];
}

interface GroupBucket {
  name: string | null;
  entries: SeasonStandingEntry[];
}

type StandingsTab = 'singles' | 'pairs';

const PAIRS_TAB_PARAM = 'debel';

function sortByPoints(entries: SeasonStandingEntry[]): SeasonStandingEntry[] {
  return [...entries].sort((a, b) => {
    if (b.countedPoints !== a.countedPoints) {
      return b.countedPoints - a.countedPoints;
    }

    if (b.totalPoints !== a.totalPoints) {
      return b.totalPoints - a.totalPoints;
    }

    return a.name.localeCompare(b.name);
  });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }

  return d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const LeagueStandings: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const seasonIdParam = searchParams.get('seasonId');
  const seasonId = seasonIdParam ? Number(seasonIdParam) : null;

  const [leagueName, setLeagueName] = useState('');
  const [seasons, setSeasons] = useState<LeagueSeasonItem[]>([]);
  const [hasRounds, setHasRounds] = useState(false);
  const [entries, setEntries] = useState<SeasonStandingEntry[]>([]);
  const [pairEntries, setPairEntries] = useState<SeasonStandingEntry[]>([]);
  const [meta, setMeta] = useState<SeasonStandingsMeta | null>(null);
  const [pairMeta, setPairMeta] = useState<SeasonStandingsMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      return;
    }

    setLoading(true);
    const standingsQuery = seasonId !== null && Number.isFinite(seasonId)
      ? `?seasonId=${seasonId}`
      : '';

    Promise.all([
      fetch(apiUrl(`/api/public/leagues/${id}`)).then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return res.json() as Promise<LeagueMeta>;
      }),
      fetch(apiUrl(`/api/public/leagues/${id}/standings${standingsQuery}`)).then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return res.json() as Promise<{
          standings: SeasonStandingEntry[];
          pairStandings: SeasonStandingEntry[];
          meta: SeasonStandingsMeta;
          pairMeta: SeasonStandingsMeta;
        }>;
      }),
    ])
      .then(([leagueData, standingsData]) => {
        setLeagueName(leagueData.name);
        setSeasons(leagueData.seasons ?? []);
        setHasRounds((leagueData.rounds ?? []).length > 0);
        setEntries(standingsData.standings ?? []);
        setPairEntries(standingsData.pairStandings ?? []);
        setMeta(standingsData.meta ?? null);
        setPairMeta(standingsData.pairMeta ?? null);
        setLoading(false);
      })
      .catch(() => {
        setError('Nie udało się załadować tabeli.');
        setLoading(false);
      });
  }, [id, seasonId]);

  const buckets = useMemo<GroupBucket[]>(() => [{ name: null, entries: sortByPoints(entries) }], [entries]);
  const pairBuckets = useMemo<GroupBucket[]>(() => [{ name: null, entries: sortByPoints(pairEntries) }], [pairEntries]);
  const currentSeason = seasonId !== null
    ? seasons.find((s) => s.id === seasonId) ?? null
    : null;

  const hasPairs = pairEntries.length > 0;
  const activeTab: StandingsTab = hasPairs && searchParams.get('tab') === PAIRS_TAB_PARAM
    ? 'pairs'
    : 'singles';

  function selectTab(tab: StandingsTab): void {
    const next = new URLSearchParams(searchParams);

    if (tab === 'pairs') {
      next.set('tab', PAIRS_TAB_PARAM);
    } else {
      next.delete('tab');
    }

    setOpenEntryId(null);
    setSearchParams(next, { replace: true });
  }

  if (loading) {
    return <div className="event-detail-loading">Ładowanie...</div>;
  }

  if (error) {
    return <div className="event-detail-error">{error}</div>;
  }

  const heading = currentSeason
    ? `${leagueName} — ${currentSeason.name}`
    : `${leagueName} — Tabela`;

  function renderTable(bucket: GroupBucket, kind: 'singles' | 'pairs') {
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #d1d5db' }}>
              <th style={thLeft}>#</th>
              <th style={thLeft}>{kind === 'pairs' ? 'Para' : 'Gracz'}</th>
              <th style={thCenter} title={`Suma punktów z max ${meta?.seasonTarget ?? ''} najlepszych kolejek${countedShare(meta?.countedPercent)}`}>Pkt</th>
              <th style={thCenter} title={kind === 'pairs' ? 'Ile kolejek deblowych para rozegrała' : 'Ile kolejek gracz rozegrał'}>Grane</th>
              <th style={thCenter} title="Ile kolejek liczy się do Pkt">Liczone</th>
            </tr>
          </thead>
          <tbody>
            {bucket.entries.map((entry, index) => {
              const allCounted = entry.countedRounds === entry.roundsPlayed;
              const dropped = entry.roundsPlayed - entry.countedRounds;
              const pktTooltip = allCounted
                ? `Wszystkie ${entry.roundsPlayed} kolejek wlicza się do Pkt (suma: ${entry.perRoundPoints.join(' + ')}).`
                : `Liczone ${entry.countedRounds} z ${entry.roundsPlayed}: ${entry.perRoundPoints.slice(0, entry.countedRounds).join(' + ')}. Dropowane ${dropped} najsłabszych.`;

              const rowKey = `${kind}:${entry.entityId}`;
              const isOpen = openEntryId === rowKey;
              const refMeta = kind === 'pairs' ? pairMeta : meta;

              return (
                <React.Fragment key={rowKey}>
                  <tr
                    onClick={() => setOpenEntryId(isOpen ? null : rowKey)}
                    style={{
                      borderBottom: '1px solid #e5e7eb',
                      background: isOpen ? '#eff6ff' : (index % 2 === 0 ? '#fff' : '#f9fafb'),
                      cursor: 'pointer',
                    }}
                  >
                    <td style={{ ...tdBase, color: '#6b7280', width: 32 }}>{entry.rank ?? index + 1}</td>
                    <td style={{ ...tdBase, fontWeight: 500 }}>
                      <EntityNameLinks
                        entry={entry}
                        kind={kind}
                        ratingDiscipline={refMeta?.ratingDiscipline ?? 'SINGLES'}
                      />
                    </td>
                    <td style={{ ...tdCenter, fontWeight: 700 }} title={pktTooltip}>
                      {entry.countedPoints}
                    </td>
                    <td
                      style={{ ...tdCenter, color: '#6b7280' }}
                      title={refMeta ? `${entry.roundsPlayed} z ${refMeta.roundsTotal} kolejek` : undefined}
                    >
                      {entry.roundsPlayed}
                      {refMeta && (
                        <span style={{ opacity: 0.5 }}> z {refMeta.roundsTotal}</span>
                      )}
                    </td>
                    <td
                      style={{ ...tdCenter, color: '#6b7280', fontWeight: 600 }}
                      title={allCounted
                        ? `Wszystkie ${entry.countedRounds} zagrane kolejki wliczone`
                        : `Liczone ${entry.countedRounds} najlepszych (dropowane ${dropped})`}
                    >
                      {entry.countedRounds}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr style={{ background: '#f1f5f9' }}>
                      <td colSpan={5} style={{ padding: '12px 16px' }}>
                        <ResultsBreakdown entry={entry} leagueId={id ?? ''} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="event-detail event-detail--standings">
      <Link to={`/leagues/${id}`} className="event-detail-back">
        ← Powrót do ligi
      </Link>
      <h1 className="event-detail-name">{heading}</h1>

      {hasPairs && (
        <div className="tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'singles'}
            className={`tab-button${activeTab === 'singles' ? ' active' : ''}`}
            onClick={() => selectTab('singles')}
          >
            Indywidualnie
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'pairs'}
            className={`tab-button${activeTab === 'pairs' ? ' active' : ''}`}
            onClick={() => selectTab('pairs')}
          >
            Debel
          </button>
        </div>
      )}

      {activeTab === 'singles' && (
        <>
          {meta && entries.length > 0 && (
            <p className="standings-note">
              Punkty: {meta.scoringRule}
              {' '}Z {meta.roundsTotal}{' '}
              {pluralPL(meta.roundsTotal, 'kolejki', 'kolejek', 'kolejek')}{' '}
              sezonu liczy się max <strong>{meta.seasonTarget} najlepszych</strong> kolejek{countedShare(meta.countedPercent)}.
              {' '}Kto zagrał mniej — bierze wszystkie swoje.
              {' '}Liczba przy nazwisku to aktualny ranking{' '}
              {meta.ratingDiscipline === 'DOUBLES' ? 'debla' : 'singla'}.
              {' '}Kliknij wiersz, żeby zobaczyć szczegóły kolejek.
            </p>
          )}

          {entries.length === 0 ? (
            <p>
              {hasRounds
                ? 'Brak rozegranych meczów do utworzenia tabeli.'
                : 'Tabela zostanie uzupełniona po zakończeniu pierwszej kolejki.'}
            </p>
          ) : (
            buckets.map((bucket) => (
              <section key={`singles:${bucket.name ?? '__no_group__'}`} style={{ marginBottom: 28 }}>
                {bucket.name && (
                  <h2 className="standings-bucket-title">
                    {bucket.name}
                    <span style={{ marginLeft: 8, color: '#9ca3af', fontWeight: 400, fontSize: 13 }}>
                      ({bucket.entries.length} {bucket.entries.length === 1 ? 'zawodnik' : 'zawodników'})
                    </span>
                  </h2>
                )}
                {renderTable(bucket, 'singles')}
              </section>
            ))
          )}
        </>
      )}

      {activeTab === 'pairs' && (
        <>
          {pairMeta && (
            <p className="standings-note">
              Punkty: {pairMeta.scoringRule}
              {' '}Z {pairMeta.roundsTotal} {pluralPL(pairMeta.roundsTotal, 'kolejki', 'kolejek', 'kolejek')} deblowych
              {' '}liczy się max <strong>{pairMeta.seasonTarget} najlepszych</strong>.
              {' '}Para = konkretna dwójka — zmiana partnera = osobny wpis.
              {' '}Kliknij wiersz, żeby zobaczyć szczegóły kolejek.
            </p>
          )}

          {pairBuckets.map((bucket) => (
            <section key={`pairs:${bucket.name ?? '__no_group__'}`} style={{ marginBottom: 28 }}>
              {bucket.name && (
                <h2 className="standings-bucket-title">
                  {bucket.name}
                  <span style={{ marginLeft: 8, color: '#9ca3af', fontWeight: 400, fontSize: 13 }}>
                    ({bucket.entries.length} {bucket.entries.length === 1 ? 'para' : 'par'})
                  </span>
                </h2>
              )}
              {renderTable(bucket, 'pairs')}
            </section>
          ))}
        </>
      )}
    </div>
  );
};

const EntityNameLinks: React.FC<{
  entry: SeasonStandingEntry;
  kind: 'singles' | 'pairs';
  ratingDiscipline: 'SINGLES' | 'DOUBLES';
}> = ({ entry, kind, ratingDiscipline }) => {
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const linkStyle: React.CSSProperties = { color: '#1f2937', textDecoration: 'none' };
  const suffix = kind === 'pairs' ? '?tab=debel' : '';
  const ratings = entry.playerRatings ?? [];

  if (entry.playerIds.length === 0) {
    return <span className="entity-name-rating">{entry.name}</span>;
  }

  const nameParts = entry.name.split(' / ');
  if (entry.playerIds.length !== nameParts.length) {
    const target = entry.playerIds.length === 1 ? `/player/${entry.playerIds[0]}${suffix}` : null;

    if (target) {
      return (
        <span className="entity-name-rating">
          <Link to={target} onClick={stop} style={linkStyle}>
            {entry.name}
          </Link>
          <InlineRatingChip rating={ratings[0] ?? null} discipline={ratingDiscipline} />
        </span>
      );
    }

    return <span className="entity-name-rating">{entry.name}</span>;
  }

  return (
    <span className="entity-name-rating">
      {entry.playerIds.map((pid, i) => (
        <React.Fragment key={pid}>
          {i > 0 && <span className="entity-name-rating__sep"> / </span>}
          <Link to={`/player/${pid}${suffix}`} onClick={stop} style={linkStyle}>
            {nameParts[i]}
          </Link>
          <InlineRatingChip rating={ratings[i] ?? null} discipline={ratingDiscipline} />
        </React.Fragment>
      ))}
    </span>
  );
};

const InlineRatingChip: React.FC<{
  rating: number | null;
  discipline: 'SINGLES' | 'DOUBLES';
}> = ({ rating, discipline }) => (
  <span
    className={`rating-inline-chip${rating == null ? ' rating-inline-chip--empty' : ''}`}
    title={
      rating == null
        ? 'Brak rankingu'
        : `Aktualny ranking ${discipline === 'DOUBLES' ? 'debla' : 'singla'}`
    }
  >
    {rating == null ? '—' : rating}
  </span>
);

const ResultsBreakdown: React.FC<{ entry: SeasonStandingEntry; leagueId: string }> = ({ entry, leagueId }) => {
  const chrono = useMemo(
    () =>
      [...entry.results].sort((a, b) => {
        const t = a.roundDate.localeCompare(b.roundDate);
        if (t !== 0) {
          return t;
        }

        return a.kolejkaNumber - b.kolejkaNumber;
      }),
    [entry.results],
  );

  if (chrono.length === 0) {
    return <span style={{ color: '#6b7280' }}>Brak wyników.</span>;
  }

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #d1d5db', color: '#6b7280' }}>
              <th style={breakHead}>Kolejka</th>
              <th style={breakHead}>Data</th>
              <th style={breakHead}>Grupa</th>
              <th style={{ ...breakHead, textAlign: 'center' }}>Pozycja</th>
              <th style={{ ...breakHead, textAlign: 'center' }}>Pkt</th>
              <th style={{ ...breakHead, textAlign: 'center' }}>Liczone?</th>
            </tr>
          </thead>
          <tbody>
            {chrono.map((r) => (
              <tr key={r.roundId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={breakCell}>
                  <Link
                    to={`/leagues/${leagueId}/rounds/${r.roundId}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}
                  >
                    K{r.kolejkaNumber}
                  </Link>
                </td>
                <td style={breakCell}>{formatDate(r.roundDate)}</td>
                <td style={breakCell}>{r.groupName ?? '—'}</td>
                <td style={{ ...breakCell, textAlign: 'center' }}>{r.position}.</td>
                <td style={{ ...breakCell, textAlign: 'center', fontWeight: r.counted ? 700 : 400, color: r.counted ? '#111827' : '#9ca3af' }}>
                  {r.points}
                </td>
                <td style={{ ...breakCell, textAlign: 'center' }}>
                  {r.counted ? (
                    <span style={{ color: '#059669', fontWeight: 600 }}>✓</span>
                  ) : (
                    <span style={{ color: '#9ca3af' }} title="Dropowana (poza najlepszymi kolejkami)">×</span>
                  )}
                </td>
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid #d1d5db', background: '#f9fafb' }}>
              <td style={{ ...breakCell, fontWeight: 600 }} colSpan={4}>Suma (liczone {entry.countedRounds} z {entry.roundsPlayed})</td>
              <td style={{ ...breakCell, textAlign: 'center', fontWeight: 700 }}>{entry.countedPoints}</td>
              <td style={breakCell}></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const thLeft: React.CSSProperties = { textAlign: 'left', padding: '6px 8px' };
const thCenter: React.CSSProperties = { textAlign: 'center', padding: '6px 8px' };
const tdBase: React.CSSProperties = { padding: '6px 8px' };
const tdCenter: React.CSSProperties = { padding: '6px 8px', textAlign: 'center' };
const breakHead: React.CSSProperties = { padding: '6px 8px', textAlign: 'left', fontWeight: 500 };
const breakCell: React.CSSProperties = { padding: '6px 8px' };

// Older API sends no share at all (then it is the classic 77%); null means the
// season counts a fixed number of kolejki instead.
function countedShare(countedPercent: number | null | undefined): string {
  if (countedPercent === null) {
    return '';
  }

  return ` (${countedPercent ?? 77}%)`;
}

export default LeagueStandings;
